<i18n>
{
  "en": {
    "title": "Editing prices of \"{product}\" in {group} group",
    "saveProductPricesError": "Error saving product prices. Please, try it later.",
    "saveProductPricesSuccess": "Saved product prices successfully",
    "revertChanges": "Revert prices to their initial state",
    "demandIndexed": "This group is affected by an indexing rule",
    "savereferenceGroup": "The price change affects another group by an indexing rule"
  },
  "es": {
    "title": "Editando precios de \"{product}\" en agrupación {group}",
    "saveProductPricesError": "Error guardando precios del producto. Inténtelo de nuevo más tarde.",
    "saveProductPricesSuccess": "Se han guardado los precios del producto correctamente",
    "revertChanges": "Revertir los precios a su estado inicial",
    "demandIndexed": "Esta agrupación está siendo afectada por una regla de indexación",
    "savereferenceGroup": "El cambio de precio afecta otra agrupación por una regla de indexación"
  }
}
</i18n>

<template>
  <v-container v-if="productRecommendation">
    <div class="text-center font-weight-bold ma-1 text-h6"> {{ title }}</div>
    <v-col class="text-right">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div class="reset-button d-inline-flex flex-grow-0" v-on="on" v-bind="attrs">
            <v-btn class="elevation-1 reset-button" @click="onReset" icon small
                   :disabled="!isConfirmationEnabled">
              <v-icon>mdi-cached</v-icon>
            </v-btn>
          </div>
        </template>
        <span>{{ this.$t('revertChanges') }}</span>
      </v-tooltip>
    </v-col>
    <price-edit-table @price-change="onUpdate"
                      :group-object="demandObject"
                      :parent-group-object="parentDemandObject"
                      :max-weight-half-portion-rate="maxWeightHalfPortionRate"
                      :maximum-price-allowed="productRecommendation.max_range_price"
                      :minimum-price-allowed="productRecommendation.min_range_price"
                      :disabled="isEditTableDisabled"
                      v-if="!useOneService"/>
    <single-service-price-edit-table @price-change="onUpdate"
                                     :group-object="demandObject"
                                     :parent-group-object="parentDemandObject"
                                     :max-weight-half-portion-rate="maxWeightHalfPortionRate"
                                     :maximum-price-allowed="productRecommendation.max_range_price"
                                     :minimum-price-allowed="productRecommendation.min_range_price"
                                     :disabled="isEditTableDisabled"
                                     v-else/>
    <dynameat-confirmation-buttons @cancel="onCancel" @confirm="onSaveTable"
                                   :confirm-enabled="isConfirmationEnabled"/>
  </v-container>
</template>

<script>

import Vue from 'vue';
import { createErrorNotification, createWarningNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationButtons from '@dynameat/components/DynameatConfirmationButtons';
import { routeOutConfirmationMixin } from '@core/mixins';
import isEqual from 'lodash/isEqual';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import {
  Components, MapActions, MapGetters, Mixins, Name
} from '@core/helpers/decorators';
import cloneDeep from 'lodash/cloneDeep';
import PriceEditTable from '../components/PriceEditTable.vue';
import SingleServicePriceEditTable from '../components/SingleServicePriceEditTable.vue';

@Component
@Components({ DynameatConfirmationButtons, PriceEditTable, SingleServicePriceEditTable })
@Mixins([routeOutConfirmationMixin])
@MapActions(['debouncedProfitabilityRequest', 'updateProductPriceRecommendation', 'addNotification'])
@MapGetters(['groupList', 'pointOfSaleSettings', 'productRecommendationMap', 'options'])
export default class RecommendationDetail extends Vue {
  @Name 'recommendation-detail';

  @Prop(Object) productRecommendation;

  @Prop(String) groupCodeName;

  @Prop(Boolean) isPeriodEditable;

  @Prop({ type: Boolean, default: false }) isReferenceGroup;

  @Prop({ type: Boolean, default: false }) isGroupIndexed;

  currentDemandObject = null;

  initialDemandObject = null;

  arePricesValid = true;

  get useOneService() {
    return this.options.find((el) => el.name === 'use_one_service_for_predictions')?.value || false;
  }

  get title() {
    if (this.groupList && this.groupList.length) {
      const group = this.groupList.find((el) => el.code_name === this.groupCodeName);
      return this.$t('title', {
        product: this.productRecommendation.commercial_name,
        group: group.translation.toLowerCase()
      });
    }
    return '';
  }

  get demandObject() {
    return this.currentDemandObject || this.productRecommendation.recommendations_by_demand[this.groupCodeName];
  }

  get parentDemandObject() {
    if (this.productRecommendation.hasParent) {
      const parent = this.productRecommendationMap.get(this.productRecommendation.parent);
      return parent.recommendations_by_demand[this.groupCodeName];
    }
    return null;
  }

  get hasChanged() {
    return !isEqual(this.currentDemandObject, this.initialDemandObject);
  }

  get isConfirmationEnabled() {
    return this.isPeriodEditable && this.hasChanged;
  }

  get maxWeightHalfPortionRate() {
    return this.pointOfSaleSettings.maxPriceHalfPortionRate;
  }

  @Watch('hasChanged')
  onHasChanged(newHasChanged) {
    this.allowRouteExitWithoutConfirmation = !newHasChanged;
  }

  created() {
    if (!this.productRecommendation) {
      this.$router.push({
        name: 'productRecommendation',
        params: { periodGuid: this.$route.params.periodGuid }
      });
    }
    if (this.isGroupIndexed) this.addNotification(createWarningNotification(this.$t('demandIndexed'), {}, 3000));
  }

  onSaveTable() {
    try {
      this.updateProductPriceRecommendation({
        productRecommendation: this.productRecommendation,
        groupCodeName: this.groupCodeName,
        demandObject: this.currentDemandObject
      });
      this.allowRouteExitWithoutConfirmation = true;
      this.debouncedProfitabilityRequest(this.$route.params.periodGuid);
      if (this.isReferenceGroup) this.addNotification(createWarningNotification(this.$t('savereferenceGroup'), {}, 3000));
      this.$router.go(-1);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('saveProductPricesError')));
    }
  }

  onUpdate(newDemandObject) {
    if (this.initialDemandObject === null) this.initialDemandObject = newDemandObject;
    this.currentDemandObject = newDemandObject;
  }

  onValidate(newValidationValue) {
    this.arePricesValid = newValidationValue;
  }

  onCancel() {
    this.$router.go(-1);
  }

  onReset() {
    this.currentDemandObject = cloneDeep(this.initialDemandObject);
  }

  get isEditTableDisabled() {
    return !this.isPeriodEditable || this.isGroupIndexed;
  }
}
</script>

<style>
.reset-button {
  margin-bottom: 1%;
  margin-right: 1%;
}
</style>
